import { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { joinApiUrl } from '@/api/url';

interface AvatarProps {
  src?: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

const sizeStyles = {
  sm: 'w-7 h-7 text-xs',
  md: 'w-8 h-8 text-sm',
  lg: 'w-10 h-10 text-base',
  xl: 'w-12 h-12 text-lg',
  '2xl': 'w-20 h-20 text-2xl',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Resolve current relative and older absolute uploaded-avatar URLs through
 * the configured API. Bundled iOS apps cannot load /api paths from their local
 * Capacitor origin. External profile pictures stay unchanged.
 */
function resolveAvatarUrl(src: string): string {
  try {
    const url = new URL(src, window.location.origin);
    const avatarPathMatch = url.pathname.match(/^\/api\/users\/avatar\/(.+)$/);
    if (avatarPathMatch) {
      return joinApiUrl(
        import.meta.env.VITE_API_URL || '/api',
        `/users/avatar/${avatarPathMatch[1]}${url.search}${url.hash}`,
      );
    }
    // External URL (Google, Gravatar, etc.) — use as-is
    return src;
  } catch {
    // Not a valid URL — return as-is
    return src;
  }
}

export default function Avatar({ src, name, size = 'md', className }: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  // Reset error state when src changes (e.g. user uploads a new avatar)
  useEffect(() => {
    setImgError(false);
  }, [src]);

  if (src && !imgError) {
    return (
      <img
        src={resolveAvatarUrl(src)}
        alt={name}
        onError={() => setImgError(true)}
        className={cn('avatar object-cover', sizeStyles[size], className)}
      />
    );
  }

  return (
    <div className={cn('avatar', sizeStyles[size], className)} title={name} aria-hidden="true">
      {getInitials(name)}
    </div>
  );
}
